<script setup lang="ts">
const { redirectToApp } = useAppRedirect()
const { appStoreLinks } = useLandingData()

// Lazy-load the 1.9MB Lottie JSON only after page renders (fallback image shows first)
const lottieData = ref<object | null>(null)
onMounted(async () => {
  const { default: data } = await import('~/assets/lottie/rintisan-billing-lottie.json')
  lottieData.value = data
})
const decoration = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M6 12H18" stroke="#BDBDBD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12 18V6" stroke="#BDBDBD" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
</script>

<template>
  <section
    id="home"
    class="grid items-center grid-cols-12 container mx-auto sm:px-4 overflow-hidden"
  >
    <div class="col-span-12 md:col-span-5">
      <div class="title-section pt-[110px] md:pt-[165px] text-center md:text-left px-[30px] pb-[30px] border-dashed border bg-[#FAFAFA] border-neutral-200 border-t-0">
        <AnimateOnScroll animation="zoom-in">
          <p class="text-lg! font-normal text-neutral-700 geistMonoFont sm:geistFont">
            Aplikasi Invoice Terintegrasi
          </p>
        </AnimateOnScroll>

        <AnimateOnScroll animation="zoom-in" :delay="100">
          <h1 class="mt-3 text-[32px] sm:text-[42px] leading-tight font-bold text-neutral-700">
            Bayar Invoice dan
            <br class="hidden sm:block">
            Terima Pembayaran
            <br class="hidden sm:block">
            Pakai Rintisan Billing
          </h1>
        </AnimateOnScroll>

        <AnimateOnScroll animation="zoom-in" :delay="200">
          <div class="flex gap-2 yellow-gradient mt-3 rounded-full w-max mx-auto md:mx-0 items-center rounded-tl-none px-[10px] py-[6px]">
            <img
              class="object-contain w-[55px] h-[30px] sm:w-[100px] sm:h-[40px]"
              src="/images/primary/thumbnail-user.png"
              alt="User Thumbnails"
              width="100"
              height="40"
              fetchpriority="high"
            >
            <p class="text-sm md:text-base font-semibold text-neutral-800">
              Join 10.000+ users
            </p>
          </div>
        </AnimateOnScroll>
      </div>

      <div class="p-[30px] relative w-full border border-dashed border-neutral-200 border-t-0">
        <div class="absolute -top-[13px] -left-[13px]" v-html="decoration" />
        <div class="absolute -right-[13px] -top-[13px]" v-html="decoration" />
        <div class="absolute -bottom-[13px] -left-[13px]" v-html="decoration" />
        <div class="absolute -bottom-[13px] -right-[13px]" v-html="decoration" />

        <div class="flex gap-6 sm:gap-2 flex-wrap justify-center md:justify-start">
          <AnimateOnScroll animation="fade-right">
            <Button
              class="h-12 sm:h-auto flex items-center gap-1 px-3 py-1 bg-black border rounded-lg border-neutral-200 hover:bg-neutral-900 transition-colors"
              aria-label="Download di App Store"
              @click="redirectToApp(appStoreLinks.ios)"
            >
              <img
                class="object-contain size-6"
                src="/images/primary/appleLogo.png"
                alt=""
                aria-hidden="true"
              >
              <div class="text-left" aria-hidden="true">
                <span class="block text-[10px] font-normal text-white">
                  Download on the
                </span>
                <span class="block text-base! font-bold text-white">
                  App Store
                </span>
              </div>
            </Button>
          </AnimateOnScroll>

          <AnimateOnScroll animation="fade-right" :delay="100">
            <Button
              class="h-12 sm:h-auto flex items-center gap-1 px-3 py-1 bg-black border rounded-lg border-neutral-200 hover:bg-neutral-900 transition-colors"
              aria-label="Download di Google Play"
              @click="redirectToApp(appStoreLinks.android)"
            >
              <img
                class="object-contain size-6"
                src="/images/primary/play-store.png"
                alt=""
                aria-hidden="true"
              >
              <div class="text-left" aria-hidden="true">
                <span class="block text-xs! font-normal text-white">
                  GET IT ON
                </span>
                <span class="block text-sm! font-bold text-white">
                  Google Play
                </span>
              </div>
            </Button>
          </AnimateOnScroll>
        </div>
      </div>

      <div class="border border-t-0 border-dashed h-[30px] md:h-[201px] border-neutral-200 border-b-0" />
    </div>

    <div class="col-span-12 md:col-span-7 mb-[94px] md:mb-0">
      <AnimateOnScroll animation="fade-left">
        <div class="block mx-auto md:ml-20">
          <ClientOnly>
            <lottie
              v-if="lottieData"
              class="w-[300px] h-[300px] md:w-[600px] md:h-[600px] mx-auto"
              :data="lottieData"
            />
            <img
              v-else
              src="/images/primary/landing-content-1.png"
              alt="Rintisan Billing App Preview"
              class="w-[300px] h-[300px] md:w-[600px] md:h-[600px] mx-auto object-contain"
              width="600"
              height="600"
              fetchpriority="high"
            >
            <template #fallback>
              <img
                src="/images/primary/landing-content-1.png"
                alt="Rintisan Billing App Preview"
                class="w-[300px] h-[300px] md:w-[600px] md:h-[600px] mx-auto object-contain"
                width="600"
                height="600"
                fetchpriority="high"
              >
            </template>
          </ClientOnly>
        </div>
      </AnimateOnScroll>
    </div>
  </section>
</template>

<style scoped>
.yellow-gradient {
  background: linear-gradient(109deg, #ffca53 -6.11%, #fddc92 50.13%, #ffefcc 100.02%, #ffedc5 127.27%);
}
</style>
